import { useStore, NetStateEnum, ValidationError, useInjectAbTest } from '@tt4b/ttam-hooks';
import { isValidId } from '@tt4b/ttam-utils';
import { computed, ref, Ref } from '@vue/composition-api';
import bcLogo from '@tt4b/ttam-assets/src/images/bc-logo.png';
import { groupBy, intersection, uniq } from 'lodash-es';
import { filterKeywords } from './../../../utils/string-utils';
import { isAssetExceedLimit } from './utils';
import { i18n } from './../../../i18n';
import {
  AdgroupFormData,
  CatalogListItem,
  CATALOG,
  AdgroupConfig,
  CatalogStoreType,
  DataSourceType,
  StoreType,
} from '@tt4b/ttam-enums';

/**
 * 获取catalog 分组名称和 icon
 * 如果资产化以 BC 分组，否则以 adv 分组
 */
const genGroupItem = catalogItem => {
  const groupName = item => {
    const { state } = useStore();
    const advertiserName = state.accountInfo.data.value?.account?.name;
    return item.bc_info?.name ?? advertiserName;
  };

  const groupImage = item => item.bc_info?.picture_url ?? bcLogo;

  return {
    ...catalogItem,
    is_group: true,
    name: groupName(catalogItem),
    picture_url: groupImage(catalogItem),
  };
};

/**
 * 展示的 catalog
 * 过滤并进行按照 bc/adv 分组
 * @param list catalog 列表
 * @returns
 */
export function useCatalogDisplayList(list) {
  const keywords = ref('');

  const filterList = computed(
    () => list?.value.filter(({ name, platformId }) => filterKeywords(`${name}${platformId}`, keywords.value)) || [],
  );

  const displayList = computed(() => {
    const arr = [];
    const groupMap = groupBy(filterList.value, item => item.bc_info.id || '0');
    Object.keys(groupMap).forEach(id => {
      const items = groupMap[id];
      if (items[0]) {
        // 插入 group 并将整个数据打平
        const groupItem = genGroupItem(items[0]);
        arr.push(groupItem, ...items);
      }
    });
    return arr;
  });

  return {
    keywords,
    filterList,
    displayList,
  };
}

/**
 * 是否展示提醒
 * @param opts
 * @returns
 */
export function usePropsVisible(opts: { catalog: Ref<CatalogListItem> }) {
  const visible = computed(() => {
    const catalog = opts.catalog.value;
    return catalog?.prod_approved_count < 1;
  });
  return {
    visible,
  };
}

/**
 * 获取操作列表
 * 由于需要触发原来的请求函数，需要作为参数传入
 * @param opts
 * @returns
 */
type Operate = () => void;
export function usePropsOperationList(opts: {
  refresh: Operate;
  create: Operate;
  selectedCatalogData: Ref<CatalogListItem>;
  isEdit: Ref<boolean>;
  hideGotoCatalog: Ref<boolean>;
  adgroupConfig?: Ref<AdgroupConfig>;
}) {
  const refreshTrackProps = {
    'data-tea-click': 'module_ad_group_refresh_shop_catalog_click',
    'data-tea-expose': 'module_ad_group_refresh_shop_catalog_impression',
    'data-tea-source': 'catalog',
  };

  const createTrackProps = {
    'data-tea-click': 'module_ad_group_manage_catalog_click',
    'data-tea-expose': 'module_ad_group_manage_catalog_impression',
  };

  const operationList = computed(() =>
    opts.selectedCatalogData?.value?.platformId && !opts.hideGotoCatalog.value
      ? [
          {
            icon: 'vi-icon2-refresh',
            click: opts.refresh,
            disabled: opts.isEdit.value || opts.adgroupConfig?.value.disableAll,
            hint: i18n.t('adgroup_convert-refresh'),
            trackProps: refreshTrackProps,
          },
          {
            icon: 'vi-icon2-share',
            click: opts.create,
            disabled: opts.isEdit.value || opts.adgroupConfig?.value.disableAll,
            hint: i18n.t('module_common_go_to_catalog'),
            trackProps: createTrackProps,
          },
        ]
      : [
          {
            icon: 'vi-icon2-refresh',
            click: opts.refresh,
            disabled: opts.isEdit.value || opts.adgroupConfig?.value.disableAll,
            hint: i18n.t('adgroup_convert-refresh'),
            trackProps: refreshTrackProps,
          },
        ],
  );
  return {
    operationList,
  };
}
/**
 * 跳转 Catalog
 * @param opt
 * @returns
 */
export function useCatalogCreate(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = opt;
  const { state } = useStore();
  const aadvid = computed(() => state.baseInfo.value.aadvid);
  const createCatalog = () => {
    const link = `${CATALOG.catalog}?aadvid=${aadvid.value}`;
    window.open(link, '_blank');
  };
  const createProduct = () => {
    const { productPlatformId, catalogAuthorizedBc } = adgroupData.value;
    const link = isValidId(productPlatformId)
      ? `${CATALOG.productSet}?id=${productPlatformId}&aadvid=${aadvid.value}&bc_id=${catalogAuthorizedBc}`
      : `${CATALOG.catalog}?aadvid=${aadvid.value}&bc_id=${catalogAuthorizedBc}`;
    window.open(link, '_blank');
  };
  const openCatalog = () => {
    const { productPlatformId, catalogAuthorizedBc } = adgroupData.value;
    const link = `${CATALOG.catalogOverview}?aadvid=${aadvid.value}&id=${productPlatformId}&bc_id=${catalogAuthorizedBc}`;
    window.open(link, '_blank');
  };
  return {
    createCatalog,
    createProduct,
    openCatalog,
  };
}

// 导出补个单测
export function formaCatalogList(list: readonly CatalogListItem[] = [], supportTypes: CatalogStoreType[]) {
  const { isFirstCtalogCreationTest } = useInjectAbTest();
  // 未资产化并且开了资产化迁移限制白名单
  const isNotCompletedCapitalized = capitalized => !capitalized;
  // 没有商品
  const isNoProductInCatalog = sum => !sum;

  const { state } = useStore();
  const areaList = (state[NetStateEnum.areaData].data?.value?.areaListForLocationTree || [])
    .filter(item => !item.disabled)
    .map(item => item.code);
  const isCountryAvailable = (item: CatalogListItem) => {
    const catalogCountries = (item.refer_catalogs || [])
      .filter(catalog => catalog.is_main)
      .map(catalog => catalog.country);
    return intersection(catalogCountries, areaList).length;
  };
  // 1.过滤麦哲伦的商品库, 没有 web url
  // 2.设置禁用状态
  // 3.按照创建时间倒序，新前旧后
  return list
    .filter(
      (item: CatalogListItem) => supportTypes.includes(item.storeType) && !(item.source === DataSourceType.MAGELLAN),
    )
    .map((item: CatalogListItem) => {
      const { available_quota = 1000, platformId, prod_sum, is_capitalized } = item;
      const isExceedLimit = isAssetExceedLimit(platformId, available_quota);
      const isNotCompleted = isNotCompletedCapitalized(is_capitalized);
      const isNoProduct = isNoProductInCatalog(prod_sum);
      const isLocationDisabled = !isCountryAvailable(item);

      const disabled = isExceedLimit || isNotCompleted || isNoProduct || isLocationDisabled;
      return {
        ...item,
        disabled,
        isExceedLimit,
        isNotCompleted,
        isNoProduct,
        isLocationDisabled,
      };
    })
    .sort(({ createTime: time1 }, { createTime: time2 }) => time2 - time1);
}
export function useDataSource(
  opt: { catalogTypes: CatalogStoreType[] } = {
    catalogTypes: [StoreType.ECOM, StoreType.ENTERTAINMENT, StoreType.FLIGHT, StoreType.HOTEL, StoreType.DESTINATION],
  },
) {
  const { getStore } = useStore();
  const {
    data,
    isLoading,
    isEmpty,
    run: getCatalogList,
    runningPromise,
    hasLoaded,
  } = getStore(NetStateEnum.catalogList);
  const catalogList = computed(() => formaCatalogList(data?.value || [], opt.catalogTypes || []));

  // 上报 catalog 不可用的原因
  const disabledReason = computed(() => {
    if (catalogList.value.length < 1) {
      return '4';
    }
    return uniq(
      catalogList.value
        .filter(item => item.disabled)
        .map(item => {
          if (item.isNotCompleted) {
            return 1;
          }
          if (item.isNoProduct) {
            return 2;
          }
          if (item.isLocationDisabled) {
            return 3;
          }
        }),
    )
      .sort()
      .join(',');
  });

  return {
    isLoading,
    isEmpty,
    catalogList,
    getCatalogList,
    runningPromise,
    hasLoaded,
    disabledReason,
  };
}

export function useRules() {
  return {
    catalogValidator: computed(() => [
      {
        validator: (rule, value, callback) => {
          const isEmpty = !value || value === '0';
          if (isEmpty) {
            return callback(
              new ValidationError({
                messageI18nKey: 'module_adgroup_platform_id_empty_error',
                message: i18n.t('module_adgroup_platform_id_empty_error'),
                moduleName: i18n.t('module_specify_product_source'),
                subModuleName: i18n.t('module_adgroup_dpa_catalogue_label'),
              }),
            );
          }
          return callback();
        },
        trigger: 'custom',
      },
    ]),
  };
}
